import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import MangaCard from '@/components/feature/MangaCard';
import { mangaList, rankings, categories, hotTags, bannerSlides } from '@/mocks/mangaData';

export default function Home() {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [rankingTab, setRankingTab] = useState<'popularity' | 'newArrivals' | 'collections'>('popularity');

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % bannerSlides.length);
  }, []);

  useEffect(() => {
    const timer = setInterval(nextSlide, 5000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  const rankingLabels: Record<string, string> = {
    popularity: '人气榜',
    newArrivals: '新作榜',
    collections: '收藏榜',
  };

  const rankingData = rankings[rankingTab];

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '女神漫画',
    url: 'https://goddessmanga.com',
    description: '女神漫画聚焦韩漫、日漫、国漫热门推荐，提供排行榜精选、免费全集下拉式在线漫画阅读，并支持追更与收藏，尽享漫画大全。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://goddessmanga.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  return (
    <>
      <SEOHead
        title="女神漫画 - 韩漫日漫国漫全集免费推荐 | 女神漫画排行榜热门精选 | 下拉式在线漫画阅读 | 漫画追更收藏大全"
        description="女神漫画聚焦韩漫、日漫、国漫热门推荐，提供排行榜精选、免费全集下拉式在线漫画阅读，并支持追更与收藏，尽享漫画大全。"
        keywords="女神漫画,韩漫,日漫,国漫,漫画推荐,排行榜,免费全集,下拉式阅读,在线漫画,追更,收藏,漫画大全"
        canonicalUrl="https://goddessmanga.com"
        schema={schema}
      />
      <ScrollToTop />
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative h-[420px] md:h-[600px] overflow-hidden">
          {bannerSlides.map((slide, index) => (
            <div
              key={slide.id}
              className={`absolute inset-0 transition-opacity duration-700 ${index === currentSlide ? 'opacity-100' : 'opacity-0'}`}
            >
              <img
                src={slide.imageUrl}
                alt={slide.title}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/60 via-foreground-950/30 to-foreground-950/70" />
            </div>
          ))}

          <div className="absolute inset-0 flex items-center justify-center px-4">
            <div className="text-center max-w-3xl w-full">
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-background-50 mb-4">
                {bannerSlides[currentSlide].title}
              </h1>
              <p className="text-base md:text-xl text-background-200 mb-3">
                {bannerSlides[currentSlide].subtitle}
              </p>
              <p className="text-sm md:text-base text-background-300 mb-8">
                {bannerSlides[currentSlide].description}
              </p>
              <div className="flex items-center justify-center gap-4 flex-wrap">
                <button
                  onClick={() => { navigate('/category/korean'); window.scrollTo({ top: 0, behavior: 'instant' }); }}
                  className="px-6 py-3 bg-primary-500 text-background-50 dark:text-foreground-950 rounded-full font-medium text-sm cursor-pointer whitespace-nowrap hover:bg-primary-600 transition-colors"
                >
                  立即阅读
                </button>
                <Link
                  to="/about"
                  className="px-6 py-3 border border-background-200/50 text-background-50 rounded-full font-medium text-sm cursor-pointer whitespace-nowrap hover:bg-background-50/10 transition-colors"
                >
                  了解更多
                </Link>
              </div>

              <div className="flex items-center justify-center gap-3 mt-10">
                {bannerSlides.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`w-2.5 h-2.5 rounded-full cursor-pointer transition-all ${index === currentSlide ? 'bg-primary-500 w-8' : 'bg-background-300/60'}`}
                    aria-label={`切换到第${index + 1}张横幅`}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Hot Tags */}
        <section className="py-6 bg-background-50 border-b border-background-200">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-sm font-medium text-foreground-700 whitespace-nowrap">热门标签：</span>
              {hotTags.map((tag) => (
                <button
                  key={tag}
                  className="px-3 py-1.5 bg-background-100 hover:bg-primary-100 text-foreground-600 hover:text-primary-700 text-xs rounded-full cursor-pointer whitespace-nowrap transition-colors"
                  onClick={() => { navigate('/category/korean'); window.scrollTo({ top: 0, behavior: 'instant' }); }}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 text-center mb-8">漫画分类</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group relative overflow-hidden rounded-xl p-6 md:p-8 h-48 flex flex-col justify-end cursor-pointer"
                  onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}
                >
                  <img
                    src={cat.bgUrl}
                    alt={`${cat.label}漫画`}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-foreground-950/20 to-transparent" />
                  <div className="relative z-10">
                    <h3 className="font-heading text-xl font-semibold text-background-50 mb-1">{cat.label}</h3>
                    <p className="text-sm text-background-300">{cat.description}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Rankings Section */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950">排行榜</h2>
              <div className="flex items-center bg-background-200 rounded-full px-1 py-1">
                {(['popularity', 'newArrivals', 'collections'] as const).map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setRankingTab(tab)}
                    className={`px-4 py-2 text-sm font-medium rounded-full cursor-pointer whitespace-nowrap transition-colors ${rankingTab === tab ? 'bg-primary-500 text-background-50 dark:text-foreground-950' : 'text-foreground-600 hover:text-foreground-800'}`}
                  >
                    {rankingLabels[tab]}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
              {rankingData.slice(0, 10).map((manga, index) => (
                <div key={manga.id} className="relative">
                  <div className={`absolute top-0 left-0 z-10 w-7 h-7 rounded-br-lg flex items-center justify-center text-xs font-bold text-background-50 ${index < 3 ? 'bg-primary-500' : 'bg-foreground-600'}`}>
                    {index + 1}
                  </div>
                  <MangaCard manga={manga} />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Hot Recommendations */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-8">
              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950">热门推荐</h2>
              <Link
                to="/category/korean"
                className="text-sm text-primary-500 hover:text-primary-600 font-medium cursor-pointer whitespace-nowrap transition-colors"
              >
                查看全部
                <div className="w-4 h-4 inline-flex items-center justify-center ml-1">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5" data-product-shop="">
              {mangaList.filter((m) => m.isHot).map((manga) => (
                <MangaCard key={manga.id} manga={manga} />
              ))}
            </div>
          </div>
        </section>

        {/* New Arrivals */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-8">
              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950">最新上架</h2>
              <Link
                to="/category/japanese"
                className="text-sm text-primary-500 hover:text-primary-600 font-medium cursor-pointer whitespace-nowrap transition-colors"
              >
                查看全部
                <div className="w-4 h-4 inline-flex items-center justify-center ml-1">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5" data-product-shop="">
              {mangaList.filter((m) => m.isNew).map((manga) => (
                <MangaCard key={manga.id} manga={manga} />
              ))}
            </div>
          </div>
        </section>

        {/* Completed Manga */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-8">已完结精选</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5" data-product-shop="">
              {mangaList.filter((m) => m.status === '已完结').map((manga) => (
                <MangaCard key={manga.id} manga={manga} />
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-14 md:py-20 bg-background-900">
          <div className="max-w-3xl mx-auto text-center px-4 md:px-6">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-background-50 mb-4">
              开启你的漫画之旅
            </h2>
            <p className="text-background-300 mb-8 leading-relaxed">
              韩漫、日漫、国漫全集免费推荐，排行榜热门精选，下拉式在线漫画阅读，追更收藏尽在女神漫画。
            </p>
            <button
              onClick={() => { navigate('/category/korean'); window.scrollTo({ top: 0, behavior: 'instant' }); }}
              className="px-8 py-3 bg-primary-500 text-background-50 dark:text-foreground-950 rounded-full font-medium text-sm cursor-pointer whitespace-nowrap hover:bg-primary-600 transition-colors"
            >
              立即开始阅读
            </button>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}